import { AxiosError } from "axios";
import { HttpStatus } from "constant";
import { RequestParams } from "generated";
import {
    ActuatorCommand,
    ActuatorCommandPatch,
    ActuatorCommandPost,
    AlertNoticePatch,
    AlertPatch,
    AlertPost,
    Deletion,
    DeletionPost,
    Group,
    MessageNoticePatch,
    MultipleDevicesConfigPatch,
    RegistryPatch,
    RegistryTypePost,
    SensorConfigPatch,
    Takeout2,
    Takeout2Post,
    UartConfig,
    UartConfigPost,
    DevicesConfigPatch,
} from "generated/models";
import { get, orderBy, size } from "lodash";
import LocalStorageService from "service/localStorageService";
import {
    getGwAndLDSUStatusCached,
    getGWReadingCached,
    getRegistryConfigCached,
    getRegistryInfoCached,
    invalidateRegistryInfo,
} from "service/queryCache/gatewayRegistryCache";
import {
    DeviceConfig,
    Gateway,
    LDSU,
    Ota,
    Registry,
    ResponseDto,
    Alert,
} from "types";
import {
    clsToAttributeTable,
    createErrorResponseStandard,
    createResponseStandard,
    getPrimaryUnit,
    getPrimaryUnitName,
    isActuator,
    isHttpSuccess,
    isSensor,
    orgId,
    orgIdQuery,
} from "utils/functions";
import {
    firmwareClient,
    gatewayClient,
    gatewayGroupClient,
} from "./gatewayServiceClients";
import AxiosInstance from "./axiosInstance";
import { getGatewayType, getValueModeOrObj, isRoomGroupZoneHub_ByObj } from "utils/gatewayFunctions";

export const addDevices = async (
    uuid: string,
    serialNumber: string,
    macAddress: string,
    gatewayName: string
) => {
    try {
        const body: RegistryTypePost = {
            gateway_id: uuid,
            serial: serialNumber,
            mac: macAddress,
            organization_id: orgId(),
            name: gatewayName,
            type: "IOT_GATEWAY",
        };
        const response = await gatewayClient.createRegisteredGw(body);
        return createResponseStandard<Registry>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const addLoRaWANNode = async (devEUI: string, gatewayName: string) => {
    try {
        const body: any = {
            organization_id: orgId(),
            name: gatewayName,
            dev_eui: devEUI,
            type: "LORA",
        };
        const response = await gatewayClient.createRegisteredGw(body);
        return createResponseStandard<Registry>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getGatewayUART = async (gatewayId: string) => {
    try {
        const response = await gatewayClient.getGwuartById(gatewayId, {
            query: {
                organization_id: orgId(),
            },
        });
        return createResponseStandard<UartConfig>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateGatewayUART = async ({
    gatewayId,
    body,
}: {
    gatewayId: string;
    body: UartConfigPost;
}) => {
    try {
        const response = await gatewayClient.updateUartById(gatewayId, body, {
            query: {
                organization_id: orgId(),
            },
        });
        return createResponseStandard<UartConfig>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getUARTnoticeConfig = async (gatewayId: string) => {
    try {
        const response = await gatewayClient.getMessageNotificationByGwId(
            gatewayId
        );

        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateUARTnoticeConfig = async ({
    gatewayId,
    data,
}: {
    gatewayId: string;
    data: MessageNoticePatch;
}) => {
    try {
        const response = await gatewayClient.updateMessageNotificationByGwId(
            gatewayId,
            data
        );

        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateSensorConfig = async (
    gatewayId: string,
    deviceId: string,
    said: string,
    body: SensorConfigPatch
) => {
    try {
        const response = await gatewayClient.updateSensorConfigBySaid(
            gatewayId,
            deviceId,
            said,
            body
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateDeviceConfigByPort = async (
    gatewayId: string,
    portNumber: string,
    body: DevicesConfigPatch
) => {
    try {
        const response = await gatewayClient.updateDeviceConfigByPort(
            gatewayId,
            portNumber,
            body
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getOtaStatus = async (
    gatewayId: string
): Promise<ResponseDto<Ota>> => {
    try {
        const response = await gatewayClient.getGwFirmwareByGwId(gatewayId, {
            query: orgIdQuery(),
        });
        return createResponseStandard<Ota>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateFirmwareUpdates = async (
    gatewayId: string,
    version: string
) => {
    try {
        const response = await gatewayClient.updateGwFirmwareByGwId(gatewayId, {
            version,
        });
        return createResponseStandard<Ota>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const createNewAlertNotification = async (
    gatewayId: string,
    deviceId: string,
    said: string,
    body: AlertPost
) => {
    try {
        const response = await gatewayClient.createAlert(
            gatewayId,
            deviceId,
            said,
            body
        );
        return createResponseStandard<Alert>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getAllAlertNotification = async (
    gatewayId: string,
    deviceId: string,
    said: string,
    params?: RequestParams
) => {
    try {
        const response = await gatewayClient.getAlertBySaid(
            gatewayId,
            deviceId,
            said,
            params
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getGatewayAlertNotification = async (
    gatewayId: string
): Promise<ResponseDto<Alert[]>> => {
    try {
        const response = await gatewayClient.getAlerts(gatewayId);
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateAlertNotification = async (
    gatewayId: string,
    deviceId: string,
    said: string,
    alertId: string,
    body: AlertPatch
) => {
    try {
        const response = await gatewayClient.updateAlertById(
            gatewayId,
            deviceId,
            said,
            alertId,
            body
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const downloadStorage = async (body: Takeout2Post) => {
    try {
        const response = await gatewayClient.createTakeoutTask2(body);
        return createResponseStandard<Takeout2>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const downloadFastStorage = async (body: Takeout2Post) => {
    try {
        const response = await gatewayClient.createTakeoutByGw(body, {
            format: "blob",
        });
        return response;
    } catch (error) {
        const errorResponse = createErrorResponseStandard(error);
        try {
            errorResponse.data = JSON.parse(await errorResponse.data.text());
        } catch {
            // Response body may be empty or not valid JSON (e.g., 429)
        }
        return errorResponse;
    }
};

export const clearGatewayData = async (
    gatewayId: string,
    body: DeletionPost
) => {
    try {
        const response = await gatewayClient.createNewDeletionTask(
            gatewayId,
            body
        );
        return createResponseStandard<Deletion>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

const getClearGatewayTasks = async (gatewayId: string) => {
    try {
        const response = await gatewayClient.getDeletionTasks(gatewayId);
        return createResponseStandard<Deletion[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getIsDeletionTaskRunning = async (gatewayId: string) => {
    try {
        const response = await getClearGatewayTasks(gatewayId);
        const { status, data } = response;
        const sortedList = orderBy(data, ["create_time"], ["desc"]);

        if (isHttpSuccess(status) && sortedList?.[0]?.status === null) {
            return true;
        }

        return false;
    } catch (error) {
        if (error instanceof AxiosError) {
            return false;
        }
        throw error;
    }
};

export const deleteRegistry = async (gatewayId: string) => {
    try {
        const response = await gatewayClient.deleteRegisteredGwById(gatewayId);
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getAllRegistry = async (organizationId: string = orgId()) => {
    try {
        const response = await gatewayClient.getRegisteredGWs({
            organization_id: organizationId,
        });
        return createResponseStandard<Registry[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getOwnedRegistry = async (orgIds: string[] = [orgId()]) => {
    try {
        const query = {
            organisation_ids: orgIds.join(","),
        };
        const response = await gatewayClient.getRegistriesByOrganisationIds(
            query
        );
        return createResponseStandard<Registry[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getOneRegistry = async (
    gatewayId: string
): Promise<ResponseDto<Registry>> => {
    try {
        const response = await gatewayClient.getRegisteredGwById(gatewayId);
        return createResponseStandard<Registry>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateOneRegistry = async (
    gatewayId: string,
    body: RegistryPatch
) => {
    try {
        const response = await gatewayClient.updateRegisteredGwById(
            gatewayId,
            body
        );
        const standardResponse = createResponseStandard(response);
        if (isHttpSuccess(standardResponse.status)) {
            void invalidateRegistryInfo(gatewayId);
        }
        return standardResponse;
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getRegistryInfo = async (
    gatewayId: string
): Promise<ResponseDto<Gateway>> => {
    // try {
    //     const response = await gatewayClient.getGwById(gatewayId);
    //     return createResponseStandard<Gateway>(response);
    // } catch (error) {
    //     return createErrorResponseStandard(error);
    // }
    return getRegistryInfoCached(gatewayId);
};

export const getRegistryConfig = async (
    gatewayId: string
): Promise<ResponseDto<DeviceConfig[]>> => {
    // try {
    //     const response = await gatewayClient.getGwConfigById(gatewayId);
    //     return createResponseStandard<DeviceConfig[]>(response);
    // } catch (error) {
    //     return createErrorResponseStandard(error);
    // }
    return getRegistryConfigCached(gatewayId, { staleTime: 5 });
};

export const updateLDSUName = async (
    gatewayId: string,
    deviceId: string,
    newName: string
) => {
    try {
        const response = await gatewayClient.updateDeviceConfigById(
            gatewayId,
            deviceId,
            {
                name: newName,
            }
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const resetGateway = async (gatewayId: string) => {
    try {
        const response = await gatewayClient.resetGwConfigById(gatewayId);
        return createResponseStandard<DeviceConfig[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const restartGateway = async (gatewayId: string) => {
    try {
        const response = await gatewayClient.updateGwStatusById(gatewayId, {
            status: "RESTART",
        });
        return createResponseStandard<string>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const identifyLDSU = async (gatewayId: string, deviceId: string) => {
    try {
        const response =
            await gatewayClient.requestTriggerDeviceBlinkingLedById(
                gatewayId,
                deviceId
            );
        return createResponseStandard<string>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getGWReading = async () => {
    // try {
    //     const response = await gatewayClient.getGwReadingByOrganizationId(
    //         orgIdQuery()
    //     );
    //     return createResponseStandard<object[]>(response);
    // } catch (error) {
    //     return createErrorResponseStandard(error);
    // }
    return getGWReadingCached({ staleTime: 2 });
};

export const getGwAndLDSUStatus = async (
    gatewayId: string,
    query: {
        time_range?: string;
    } = {}
) => {
    // try {
    //     const response = await gatewayClient.getDevicesReadingByGwId(
    //         gatewayId,
    //         query
    //     );
    //     return createResponseStandard<object[]>(response);
    // } catch (error) {
    //     return createErrorResponseStandard(error);
    // }
    return getGwAndLDSUStatusCached(gatewayId, query, { staleTime: 2 });
};

export const scanGateway = async (
    gatewayId: string,
    portNumber?: number,
    powerCycle: boolean = true
) => {
    try {
        const response = await gatewayClient.updateGwById(gatewayId, {
            PORT: portNumber,
            power_cycle: powerCycle,
        });
        return createResponseStandard<string>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const toggleBusPower = async (
    gatewayId: string,
    power: boolean,
    portNumber: number = 1
) => {
    try {
        const res = await gatewayClient.changePowerStateByPort(
            gatewayId,
            portNumber.toString(),
            {
                power,
            }
        );
        return createResponseStandard(res);
    } catch (err) {
        return createErrorResponseStandard(err);
    }
};

export const getAllLastBusReading = async (
    gatewayId: string,
    portNumber: string,
    timeRange?: string,
    offset?: string
) => {
    try {
        const response = await gatewayClient.getDevicesReadingByPort(
            gatewayId,
            portNumber,
            {
                time_range: timeRange,
                offset,
            }
        );
        return createResponseStandard<object[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getSensorLastReading = async (
    gatewayId: string,
    deviceId: string,
    timeRange?: string,
    offset?: string
) => {
    try {
        const response = await gatewayClient.getDevicesReadingByDeviceId(
            gatewayId,
            deviceId,
            {
                time_range: timeRange,
                offset,
            }
        );
        return createResponseStandard<object[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getDeviceConfigById = async (
    gatewayId: string,
    deviceId: string,
    params?: RequestParams
) => {
    try {
        const response = await gatewayClient.getDeviceConfigById(
            gatewayId,
            deviceId,
            params
        );
        return createResponseStandard<DeviceConfig>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateDeviceConfigById = async (
    gatewayId: string,
    deviceId: string,
    body: any
) => {
    try {
        const response = await gatewayClient.updateDeviceConfigById(
            gatewayId,
            deviceId,
            body
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getSensorConfig = async (
    gatewayId: string,
    deviceId: string,
    said: string
) => {
    try {
        const response = await gatewayClient.getSensorConfigById(
            gatewayId,
            deviceId,
            said
        );
        return createResponseStandard<object>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getRegistryGroups = async () => {
    try {
        const response = await gatewayGroupClient.getGwGroups(orgIdQuery());
        return createResponseStandard<Group[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const deleteRegistryGroup = async (groupId: string) => {
    try {
        const response = await gatewayGroupClient.deleteGwGroupById(groupId);
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const addRegistryGroup = async (groupName: string) => {
    try {
        const response = await gatewayGroupClient.createGwGroup({
            ...orgIdQuery(),
            name: groupName,
        });
        return createResponseStandard<Group>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const addRegistryToGroup = async (
    groupId: string,
    gatewayList: Array<string>
) => {
    try {
        const response = await gatewayGroupClient.updateGwGroupById(groupId, {
            add: gatewayList,
        });
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getOneGroupDetails = async (groupId: string) => {
    try {
        const response = await gatewayGroupClient.getGwGroupById(groupId);
        return createResponseStandard<Group>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateGatewayGroupName = async (
    groupId: string,
    groupName: string
) => {
    try {
        const response = await gatewayGroupClient.updateGwGroupById(groupId, {
            name: groupName,
        });
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateGatewayGroupFirmware = async (
    groupId: string,
    body: {
        selected: string[];
        version: string;
    }
) => {
    try {
        const response = await gatewayGroupClient.updateGwGroupFirmwareByGwId(
            groupId,
            body
        );
        return createResponseStandard<Ota[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const deleteGatewayFromGroup = async (
    groupId: string,
    deviceId: string
) => {
    try {
        const response = await gatewayGroupClient.updateGwGroupById(groupId, {
            remove: [deviceId],
        });
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const setGatewaysToGroup = async (
    groupId: string,
    deviceList: Array<string>
) => {
    try {
        const response = await gatewayGroupClient.updateGwGroupById(groupId, {
            add: deviceList,
        });
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getAllFirmwareList = async () => {
    try {
        const response = await firmwareClient.getFirmware();
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const deleteLDSBus = async (gatewayId: string, port: string) => {
    try {
        const response = await gatewayClient.deleteDeviceByPort(
            gatewayId,
            port,
            {
                query: orgIdQuery(),
            }
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const deleteOneLDSU = async (gatewayId: string, deviceId: string) => {
    try {
        const response = await gatewayClient.deleteDeviceById(
            gatewayId,
            deviceId,
            {
                query: orgIdQuery(),
            }
        );
        return createResponseStandard(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getActuatorCommand = async (
    gatewayId: string,
    deviceId: string,
    said: string
) => {
    try {
        const response = await gatewayClient.getActuatorConfigBySaid(
            gatewayId,
            deviceId,
            said
        );
        return createResponseStandard<ActuatorCommand>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateActuatorCommand = async (
    gatewayId: string,
    deviceId: string,
    said: string,
    body: ActuatorCommandPatch
) => {
    try {
        const response = await gatewayClient.updateActuatorConfigBySaid(
            gatewayId,
            deviceId,
            said,
            body
        );
        return createResponseStandard<ActuatorCommand>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const toggleActuatorActivation = async (body: {
    gatewayId: string;
    deviceId: string;
    said: string;
    activate: boolean;
    immediately?: boolean;
    polarity?: boolean;
}) => {
    try {
        const response = await gatewayClient.toggleActuatorBySaid(
            body.gatewayId,
            body.deviceId,
            body.said,
            {
                activate: body.activate,
                immediately: body.immediately,
                polarity: body.polarity,
            }
        );
        return createResponseStandard<ActuatorCommandPost>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const updateSensorNotice = async (
    gatewayId: string,
    deviceId: string,
    said: string,
    alertId: string,
    body: AlertNoticePatch
) => {
    try {
        const response = await gatewayClient.updateNotificationById(
            gatewayId,
            deviceId,
            said,
            alertId,
            body
        );
        return createResponseStandard<AlertNoticePatch>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

const forIOControllerTransform: (data: any) => any = (data) => {
    const appData = data.APP[data.SAID];

    // handle exception for Generic digital output (e.g. DIO Output 2) that have no UNITS []
    if (appData) {
        return {
            units: appData?.UNITS ?? ["na"],
            unit: getPrimaryUnit(appData) ?? "",
            APPID: appData?.APPID,
            min: getPrimaryUnitName(appData).MIN,
            max: getPrimaryUnitName(appData).MAX,
            accuracy: getPrimaryUnitName(appData).ACCURACY,
        };
    }
};

export const transformDeviceList = (
    deviceList: any,
    configList: DeviceConfig[],
    registry: { name: string; gateway_id: string }
) => {
    // Map for Sensor list
    let jsonList = LocalStorageService.getItem("listOfJSON");
    // Map with json File
    let filteredList: any[] = [];
    deviceList.forEach((device: any) => {
        configList.some((config: DeviceConfig) => {
            if (device.UID === config.UID) {
                let body = {
                    // Take from device
                    UID: device.UID,
                    product_name: device.NAME,
                    product_nick: device.NICK,
                    OBJ: device.OBJ,
                    bus: device.PORT,
                    APP: device.APP || null,

                    // Take from config
                    editable_name: config.name,
                    SAID: config.SAIDS,
                    sensor_name: config.sensor_names,
                    INTVL: config.INTVL,
                    MODES: config.MODES,

                    // Take from registry
                    gatewayName: registry.name,
                    gatewayId: registry.gateway_id,
                };
                filteredList.push(body);

                // return true to break the loop and early return
                return true;
            }
            // return false to continue the loop
            return false;
        });
    });
    let sensorFilteredLst: any = [];
    const classIds = clsToAttributeTable();
    filteredList.forEach((device: any) => {
        jsonList.some((j: any) => {
            if (device.OBJ === j.OBJ) {
                for (let k = 0; k < device.SAID.length; k++) {
                    let body: any = {

                        // Take from device
                        ...device,
                        APP: device.APP,
                        ldsu_uuid: device.UID,
                        gatewayName: device.gatewayName,
                        SAID: k,
                        name: device.editable_name || device.sensor_name[k],
                        device_name: device.sensor_name[k] ?? device.product_nick,
                        device_type: device.product_name,
                        sensor_type: device?.APP
                            ? device?.APP[k]?.NAME
                            : get(classIds, j.SNS[k]?.CLS) ?? device.product_name,
                        bus: device.bus,
                        gatewayId: device.gatewayId,
                        OBJ: device.OBJ,
                        INTVL: device.INTVL,

                        // Take from json file
                        CLS: j.SNS[k]?.CLS,
                        unit: j.SNS[k]?.UNIT,
                        min: j.SNS[k]?.MIN,
                        max: j.SNS[k]?.MAX,
                        accuracy: j.SNS[k]?.ACCURACY,
                        mode: j.SNS[k]?.MODE ?? null,
                        format: j.SNS[k]?.FORMAT,
                    };

                    if (body.APP) {
                        if (!size(body.APP[k])) {
                            continue;
                        }

                        body = {
                            ...body,
                            ...forIOControllerTransform(body),
                        };
                    }
                    sensorFilteredLst.push(body);
                }
                return true;
            }
            return false;
        });
    });

    return sensorFilteredLst;
};

export const getDeviceListByGatewayId = async (gatewayId: string) => {
    try {
        const [configRes, infoRes] = await Promise.all([
            getRegistryConfig(gatewayId),
            getRegistryInfo(gatewayId),
        ]);
        if (isHttpSuccess(configRes.status) && isHttpSuccess(infoRes.status)) {
            return transformDeviceList(
                get(infoRes, "data.devices", []),
                get(configRes, "data", []),
                get(infoRes, "data.registry", {
                    name: "",
                    gateway_id: "",
                })
            );
        }
        return [];
    } catch (error) {
        console.error(`Get sensors of gatewayID: ${gatewayId} error`);
        throw error;
    }
};

const transformLDSUList = async (
    infoList: any,
    configList: DeviceConfig[],
    gatewayData: Gateway
): Promise<LDSU[]> => {
    const ldsuList: LDSU[] = [];

    configList.forEach((i: any) => {
        const infoItem = infoList.find(
            (j: any) => j.UID === i.UID && !isRoomGroupZoneHub_ByObj(j.OBJ)
        );

        if (!infoItem) {
            return;
        }

        ldsuList.push({
            ldsu_uuid: i.UID,
            gatewayName: gatewayData.registry?.name,
            name: i.name,
            gatewayId: gatewayData.registry?.gateway_id,
            gatewayType: getGatewayType(gatewayData),
            bus: infoItem?.PORT,
        });
    });

    return ldsuList;
};

const getLDSUListByGatewayId = async (gatewayId: string): Promise<LDSU[]> => {
    try {
        const configRes = await getRegistryConfig(gatewayId);
        const infoRes = await getRegistryInfo(gatewayId);
        if (
            configRes.status === HttpStatus.OK &&
            infoRes.status === HttpStatus.OK &&
            infoRes.data
        ) {
            return transformLDSUList(
                get(infoRes, "data.devices", []),
                get(configRes, "data", []),
                infoRes.data
            );
        }
        return [];
    } catch (error) {
        console.error(`Get sensors of gatewayID: ${gatewayId} error`);
        throw error;
    }
};

export const getLDSUListByGatewayIds = async (
    gatewayIds: string[] = []
): Promise<LDSU[]> => {
    try {
        if (!gatewayIds || gatewayIds.length === 0) {
            const resAllRedisTry = await getAllRegistry();
            if (resAllRedisTry.status === HttpStatus.OK) {
                const { data = [] } = resAllRedisTry;
                gatewayIds = data.map(({ gateway_id }: any) => gateway_id);
            }
        }
        const sensorListArr = await Promise.all(
            gatewayIds.map((gateway_id: string) =>
                getLDSUListByGatewayId(gateway_id)
            )
        );

        return (sensorListArr || []).flat();
    } catch (error) {
        console.error(`get LDSU List By GatewayIds error`);
        throw error;
    }
};

const getDeviceListByGatewayIds = async (gatewayIds: string[] = []) => {
    try {
        if (!gatewayIds || gatewayIds.length === 0) {
            const resAllRedisTry = await getAllRegistry();
            if (resAllRedisTry.status === HttpStatus.OK) {
                const { data = [] } = resAllRedisTry;
                gatewayIds = data.map(({ gateway_id }: any) => gateway_id);
            }
        }
        const sensorListArr = await Promise.all(
            gatewayIds.map((gateway_id: string) =>
                getDeviceListByGatewayId(gateway_id)
            )
        );

        return (sensorListArr || []).flat();
    } catch (error) {
        console.error(`get Device List By GatewayIds error`);
        throw error;
    }
};

export const getSensorListByGatewayIds = async (gatewayIds: string[] = []) =>
    (await getDeviceListByGatewayIds(gatewayIds)).filter(({ CLS }: any) =>
        isSensor(CLS)
    );

export const getActuatorListByGatewayIds = async (gatewayIds: string[] = []) =>
    (await getDeviceListByGatewayIds(gatewayIds)).filter(({ CLS }: any) =>
        isActuator(CLS)
    );

export const updateMultipleDevicesConfig = async (
    data: MultipleDevicesConfigPatch
) => {
    try {
        const res = await gatewayClient.updateMultipleDevicesConfig(data);
        return createResponseStandard(res);
    } catch (err) {
        return createErrorResponseStandard(err);
    }
};

export const fetchDetailDevice = async (data: {
    gateway_id: string;
    device_id: string;
    said?: string | number;
}) => {
    const [registryInfo, registryConfig, allLdsuStatus] = await Promise.all([
        getRegistryInfo(data.gateway_id),
        getRegistryConfig(data.gateway_id),
        getGwAndLDSUStatus(data.gateway_id, { time_range: "24h" }),
    ]);

    if (
        [
            registryInfo.status,
            registryConfig.status,
            allLdsuStatus.status,
        ].includes(HttpStatus.FORBIDDEN)
    ) {
        return registryInfo || registryConfig || allLdsuStatus;
    }

    const device = get(registryInfo, "data.devices", []).find(
        (device: any) => device.UID === data.device_id
    );
    const deviceConfig = get(registryConfig, "data", []).find(
        (device: any) => device.UID === data.device_id
    );
    const deviceStatus = get(allLdsuStatus, "data.devices", []).find(
        (device: any) => device.UID === data.device_id
    );

    const deviceInfo = {
        ...device,
        ...deviceConfig,
    };

    const infoJson = LocalStorageService.getItem("listOfJSON").find(
        (json: any) => json.OBJ === deviceInfo.OBJ
    );

    const metricInfo = LocalStorageService.getItem("metricInfo");

    let deviceData = {} as any;

    if (data.said) {
        const infoJsonDevice = infoJson?.SNS?.find(
            (json: any) => json.SAID === data.said
        );

        deviceData = {
            MIN: get(deviceInfo, `min.${data.said}`),
            MAX: get(deviceInfo, `max.${data.said}`),
            MODE: get(deviceInfo, `MODES.${data.said}`),
            SAID: data.said,
            deviceInfo: infoJsonDevice,
            device_name: deviceInfo.editable_name,
            device_id: deviceInfo.UID,
            type: deviceInfo?.APP
                ? metricInfo.app[deviceInfo.APP[data.said].APPID].name
                : metricInfo.cls[infoJsonDevice?.CLS]?.name,
            readings: {
                SAID: data.said,
                lowest: get(deviceStatus, `min.${data.said}`),
                highest: get(deviceStatus, `max.${data.said}`),
                value: get(deviceStatus, `value.${data.said}`),
                timestamp: get(deviceStatus, "ts"),
            },
            port: deviceInfo.PORT,
        };
    }

    return {
        ...deviceInfo,
        gateway_name: get(registryInfo, "data.registry.name"),
        ...deviceData,
        infoJson,
    };
};

export const fetchDetailDevices = async (
    data: {
        gateway_id: string;
        device_id: string;
        said?: string;
    }[]
) => {

    const uniqueGatewayIds = Array.from(
        new Set(data.map((item) => item.gateway_id))
    );;
    let gatewayData = {} as any;
    let promises: Promise<any>[] = [];
    uniqueGatewayIds.forEach((gateway_id) => {
        promises.push(new Promise((resolve) => {
            Promise.all([
                getRegistryInfo(gateway_id),
                getRegistryConfig(gateway_id),
                getGwAndLDSUStatus(gateway_id, { time_range: "24h" }),
            ])
                .then(([registryInfo, registryConfig, allLdsuStatus]) => {
                    if (
                        [
                            registryInfo.status,
                            registryConfig.status,
                            allLdsuStatus.status,
                        ].includes(HttpStatus.FORBIDDEN)
                    ) {
                        return resolve(null);
                    }

                    gatewayData[gateway_id] = {
                        devices: get(registryInfo, "data.devices", []),
                        deviceConfigs: get(registryConfig, "data", []),
                        registryName: get(registryInfo, "data.registry.name"),
                        deviceStatus: get(allLdsuStatus, "data.devices", [])
                    };
                    resolve(null);
                });
        }));
    });

    await Promise.all(promises);

    const devices = data.map(({ gateway_id, device_id, said }) => {
        if (gatewayData[gateway_id] === undefined) {
            return null;
        }

        const registryInfo = gatewayData[gateway_id];

        const device = get(registryInfo, "devices", []).find(
            (device: any) => device.UID === device_id
        );
        const deviceConfig = get(registryInfo, "deviceConfigs", []).find(
            (device: any) => device.UID === device_id
        );
        const deviceStatus = get(registryInfo, "deviceStatus", []).find(
            (device: any) => device.UID === device_id
        );

        const deviceInfo = {
            ...device,
            ...deviceConfig,
        };

        const infoJson = LocalStorageService.getItem("listOfJSON").find(
            (json: any) => json.OBJ === deviceInfo.OBJ
        );

        const metricInfo = LocalStorageService.getItem("metricInfo");

        let deviceData = {
            deviceName: deviceInfo.NICK,
            deviceType: deviceInfo.NAME,
            deviceId: deviceInfo.UID,
            gatewayId: gateway_id,
            port: deviceInfo.PORT,
            obj: deviceInfo.OBJ,
            intvl: deviceInfo.INTVL,
            said: said,
            readings: {
                said: said,
                lowest: get(deviceStatus, `min.${said}`),
                highest: get(deviceStatus, `max.${said}`),
                value: get(deviceStatus, `value.${said}`),
                lastActive: get(deviceStatus, "ts"),
            },
        } as any;

        if (said) {
            const infoJsonDevice = infoJson?.SNS?.find(
                (json: any) => json.SAID === said
            );

            // Only proceed if we found the sensor info
            if (infoJsonDevice) {
                const mode = get(deviceInfo, `MODES.${said}`);
                const app = deviceInfo?.APP ? deviceInfo.APP[said] : null;
                const min = getValueModeOrObj(infoJsonDevice, app, "MIN", mode);
                const max = getValueModeOrObj(infoJsonDevice, app, "MAX", mode);
                const format = getValueModeOrObj(
                    infoJsonDevice,
                    app,
                    "FORMAT",
                    mode
                );
                const accuracy = getValueModeOrObj(
                    infoJsonDevice,
                    app,
                    "ACCURACY",
                    mode
                );

                deviceData = {
                    ...deviceData,
                    said: said,
                    deviceInfo: infoJsonDevice,
                    sensorName: deviceInfo.sensor_names[said] ?? infoJsonDevice.NAME,
                    sensorType: deviceInfo?.APP
                        ? deviceInfo.APP[said].NAME
                        : metricInfo.cls[infoJsonDevice.CLS]?.name,
                    min: Number(min),
                    max: Number(max),
                    mode: mode,
                    unit: infoJsonDevice.UNIT,
                    format: format,
                    accuracy: Number(accuracy),
                    metric: metricInfo.cls[infoJsonDevice.CLS]?.metric || "",
                    cls: infoJsonDevice.CLS,
                };
            }
        }

        return {
            ...deviceInfo,
            // @deprecated use gatewayName instead
            gateway_name: get(registryInfo, "registryName"),
            gatewayName: get(registryInfo, "registryName"),
            ...deviceData,
            infoJson,
        };
    });

    return devices;
};

/**
 * Fetch self-check error logs (journals) for a gateway
 * @param gatewayId - The gateway ID
 * @param params - Optional query parameters (start, end dates)
 * @returns Promise with journal entries
 */
export const getSelfCheckErrorLogs = async (
    gatewayId: string,
    params?: {
        start?: string;
        end?: string;
    }
) => {
    try {
        const response = await AxiosInstance.get(
            `/gateways/${gatewayId}/journals`,
            {
                params,
            }
        );
        return createResponseStandard(response);
    } catch (err) {
        return createErrorResponseStandard(err as AxiosError);
    }
};
